import { Component, h, Prop, Element } from '@stencil/core';
import { getClassListString } from '../component-utils/stencilUtils';
import { AafpDateLink } from '../aafp-date-link/aafp-date-link';
import { AafpTextLink } from '../aafp-text-link/aafp-text-link';
import { AafpImageLink } from '../aafp-image-link/aafp-image-link';
import { AafpHorizontalRuleLink } from '../aafp-horizontal-rule-link/aafp-horizontal-rule-link';

export declare interface LinkedListItemDetails {
  displayDate: string;
  imagePath: string;
  description: string;
  url: string;
  text: string;
  subText?: string;
}

const LINK = 'LINK';
const DATE = 'DATE';
const IMAGE_WITH_DATE = 'IMAGE_WITH_DATE';
const IMAGE_WITHOUT_DATE = 'IMAGE_WITHOUT_DATE';
const TEXT = 'TEXT';
const TEXT_HORIZONTAL_RULE = 'TEXT_HORIZONTAL_RULE';
const TEXT_WITH_DESCRIPTION = 'TEXT_WITH_DESCRIPTION';
const TEXT_WITH_DATE_AND_DESCRIPTION = 'TEXT_WITH_DATE_AND_DESCRIPTION';
const TEXT_WITH_METADATA = 'TEXT_WITH_METADATA';

/**
 * COMPNAME is for displaying a list of links.
 *
 * <em>Supported List Style Types:</em>
 * <ul>
 *   <li><a href="#LINK">LINK (DEFAULT)</a>: Displays basic links in three columns</li>
 *   <li><a href="#TEXT">TEXT</a>: Displays basic links in one column</li>
 *   <li><a href="#TEXT_HORIZONTAL_RULE">TEXT_HORIZONTAL_RULE</a>: Displays basic links in one column with horizontal rule styling</li>
 *   <li><a href="#DATE">DATE</a>: Displays links next to date in one column</li>
 *   <li><a href="#TEXT_WITH_DESCRIPTION">TEXT_WITH_DESCRIPTION</a>: Displays links with link description in one column</li>
 *   <li><a href="#TEXT_WITH_DATE_AND_DESCRIPTION">TEXT_WITH_DATE_AND_DESCRIPTION</a>: Displays links with date and link description in one column</li>
 *   <li><a href="#IMAGE_WITH_DATE">IMAGE_WITH_DATE</a>: Displays links with image, link description and date in one column</li>
 *   <li><a href="#IMAGE_WITHOUT_DATE">IMAGE_WITHOUT_DATE</a>: Displays links with image and link description in one column</li>
 * </ul>
 * For more information on supported list styles see also:
 * AEM Component- aafp-aem-library.git/ui.apps/src/main/content/jcr_root/apps/brand/aafp/components/content/aafp-link-pile
 * Sling Model- core.src.main.java.org.aafp.aem.core.models.AafpLinkPileModel.java
 */
@Component({
  tag: 'aafp-link-pile',
  shadow: false,
})
export class AafpLinkPile {
  /**
   * The display title shown above the list of links
   */
  @Prop() dataLinkedListTitle: string;

  /**
   * The heading level for the title
   */
  @Prop() dataLinkedListHeadingLevel: 'h2' | 'h3' | 'h4';

  /**
   * The image size
   */
  @Prop({ mutable: true }) dataLinkedListImageSize: string = '';

  /**
   * Stringified array of LinkedListItemDetails key-value objects.  See "Requirements for data-linked-list strings" for details on the expected data-structure.
   */
  @Prop() dataLinkedList!: string;
  /**
   * The display type for links inside the link pile. Must be one of the supported list types.  If no type is provided then display type will default to links in three columns with no details.
   */
  @Prop({ mutable: true }) dataLinkedListType:
    | 'LINK'
    | 'DATE'
    | 'IMAGE_WITH_DATE'
    | 'IMAGE_WITHOUT_DATE'
    | 'TEXT'
    | 'TEXT_HORIZONTAL_RULE'
    | 'TEXT_WITH_DESCRIPTION'
    | 'TEXT_WITH_DATE_AND_DESCRIPTION'
    | 'TEXT_WITH_METADATA' = 'LINK';
  /**
   * If value is "true" the links will be automatically generated from the parsed JSON values of the dataLinkedList property.  If value is "false" then the component expects that an aafp-link component will be rendered under the data-links slot for every item in the data-linked-list JSON string.
   */
  @Prop({ mutable: true }) createLinksFromJson: boolean = false;
  @Element() element: HTMLElement;

  /**
   * Determine the number of links per column
   * and map link element DOM references to columns
   *
   * @param totalLinks (the total length of the link pile list)
   * @returns key value pair storing aafp-link web component DOM references
   * for each column
   */
  mapLinksToColumns(totalLinks: number): { [key: number]: HTMLElement[] } {
    const linkTagElements = this.element.querySelectorAll('aafp-link');
    const columns: { [key: number]: number } = {};

    // approximate number of links per column - give or take 1
    const val = totalLinks / 3;

    // return early if total divides evenly into 3 columns
    if (totalLinks % 3 === 0) {
      columns[1] = val;
      columns[2] = val;
      columns[3] = val;
      return this.populateColumns(columns, Array.from(linkTagElements));
    }

    // use the remainder value of the average column length
    // to set the length of the 2nd and 3rd columns
    // based on whether decimal rounds up or down to next whole number
    const rounded = val - Math.floor(val) > 0.5 ? 'up' : 'down';

    if (rounded === 'up') {
      columns[1] = Math.ceil(val);
      columns[2] = Math.ceil(val);
      columns[3] = Math.ceil(val) - 1;
    }

    if (rounded === 'down') {
      columns[1] = Math.ceil(val);
      columns[2] = Math.ceil(val) - 1;
      columns[3] = Math.ceil(val) - 1;
    }

    return this.populateColumns(columns, Array.from(linkTagElements));
  }

  /**
   * Splice the array of authored links
   * into columns by index (1, 2 or 3)
   * @param columns
   * @param links
   */
  populateColumns(colLengths: { [key: number]: number }, links: HTMLElement[]): { [key: number]: HTMLElement[] } {
    const cols: { [key: number]: HTMLElement[] } = {};
    Object.keys(colLengths).map((key: string) => {
      const column = parseInt(key, 10); // i.e. 1st, 2nd, or 3rd column
      cols[column] = links.splice(0, colLengths[column]);
    });
    return cols;
  }

  componentWillLoad() {
    // different default heading level based on type
    if (this.dataLinkedListTitle && !this.dataLinkedListHeadingLevel) {
      this.dataLinkedListHeadingLevel = this.dataLinkedListType === LINK ? 'h3' : 'h4';
    }
  }

  /**
   * Render simple links in three columns on desktop
   */
  renderLinkColumns(links: Partial<LinkedListItemDetails>[]) {
    const mappedLinks: { [key: number]: HTMLElement[] } = this.mapLinksToColumns(links.length);
    return (
      <div class="adl-link-pile__container">
        <aafp-link-pile-column nodes={mappedLinks[1]} class="adl-link-pile__column"></aafp-link-pile-column>
        <aafp-link-pile-column nodes={mappedLinks[2]} class="adl-link-pile__column"></aafp-link-pile-column>
        <aafp-link-pile-column nodes={mappedLinks[3]} class="adl-link-pile__column"></aafp-link-pile-column>
      </div>
    );
  }

  renderLinksWithDate(linkDetails: LinkedListItemDetails[], aafpLinks: HTMLElement[]) {
    return linkDetails.map((link: LinkedListItemDetails, index: number) => (
      <AafpDateLink
        key={`${link.text}_${index}`}
        link={aafpLinks.find((linkTag) => linkTag.getAttribute('data-url') === link.url)!}
        date={link.displayDate}
      ></AafpDateLink>
    ));
  }

  renderLinksWithImage(linkDetails: LinkedListItemDetails[], aafpLinks: HTMLElement[], displayDate: boolean) {
    return linkDetails.map((link: LinkedListItemDetails, index: number) => (
      <AafpImageLink
        key={`${link.text}_${index}`}
        link={aafpLinks.find((aafpLink) => aafpLink.getAttribute('data-url') === link.url)!}
        image={link.imagePath}
        date={displayDate ? link.displayDate : null}
        description={link.description}
        imageSize={this.dataLinkedListImageSize}
      ></AafpImageLink>
    ));
  }

  renderLinksWithHorizontalRule(links: LinkedListItemDetails[], aafpLinks: HTMLElement[]) {
    return links.map((link: LinkedListItemDetails, index: number) => (
      <AafpHorizontalRuleLink
        key={`${link.text}_${index}`}
        link={aafpLinks.find((aafpLink) => aafpLink.getAttribute('data-url') === link.url)!}
        firstListItem={index === 0}
        url={link.url}
      ></AafpHorizontalRuleLink>
    ));
  }

  renderLinksWithText(linkDetails: LinkedListItemDetails[], aafpLinks: HTMLElement[]) {
    return linkDetails.map((link: LinkedListItemDetails, index: number) => (
      <AafpTextLink
        key={`${link.text}_${index}`}
        link={aafpLinks.find((aafpLink) => aafpLink.getAttribute('data-url') === link.url)!}
      ></AafpTextLink>
    ));
  }

  renderLinksWithDescription(linkDetails: LinkedListItemDetails[], aafpLinks: HTMLElement[]) {
    return linkDetails.map((link: LinkedListItemDetails, index: number) => (
      <AafpTextLink
        key={`${link.text}_${index}`}
        link={aafpLinks.find((aafpLink) => aafpLink.getAttribute('data-url') === link.url)!}
        description={link.description}
      ></AafpTextLink>
    ));
  }

  renderLinksWithDateAndDescription(linkDetails: LinkedListItemDetails[], aafpLinks: HTMLElement[]) {
    return linkDetails.map((link: LinkedListItemDetails, index: number) => (
      <AafpDateLink
        key={`${link.text}_${index}`}
        link={aafpLinks.find((aafpLink) => aafpLink.getAttribute('data-url') === link.url)!}
        date={link.displayDate}
        description={link.description}
      ></AafpDateLink>
    ));
  }

  renderLinksWithMetadata(linkDetails: LinkedListItemDetails[], aafpLinks: HTMLElement[]) {
    return linkDetails.map((link: LinkedListItemDetails, index: number) => (
      <AafpTextLink
        key={`${link.text}_${index}`}
        link={aafpLinks.find((aafpLink) => aafpLink.getAttribute('data-url') === link.url)!}
        date={link.displayDate}
        description={link.description}
        subText={link.subText}
      ></AafpTextLink>
    ));
  }

  getLinkElementsFromJson(links: LinkedListItemDetails[]) {
    return links.map((linkDetails: LinkedListItemDetails) => {
      const aafpLinkProps = { 'data-text': linkDetails.text, 'data-url': linkDetails.url };
      const aafpLink = document.createElement('aafp-link');
      Object.entries(aafpLinkProps).forEach((prop) => {
        aafpLink.setAttribute(prop[0], prop[1]);
      });
      return aafpLink;
    });
  }

  renderLinkPileContainer() {
    const links = JSON.parse(this.dataLinkedList);
    const createLinks = this.createLinksFromJson;
    const linkElements = createLinks
      ? this.getLinkElementsFromJson(links)
      : Array.from(this.element.querySelectorAll('aafp-link'));
    if (createLinks) linkElements.forEach((aafpLink: HTMLAafpLinkElement) => this.element.appendChild(aafpLink));
    switch (this.dataLinkedListType) {
      case DATE:
        return this.renderLinksWithDate(links, linkElements);
      case IMAGE_WITH_DATE:
        return this.renderLinksWithImage(links, linkElements, true);
      case IMAGE_WITHOUT_DATE:
        return this.renderLinksWithImage(links, linkElements, false);
      case TEXT:
        return this.renderLinksWithText(links, linkElements);
      case TEXT_HORIZONTAL_RULE:
        return this.renderLinksWithHorizontalRule(links, linkElements);
      case TEXT_WITH_DESCRIPTION:
        return this.renderLinksWithDescription(links, linkElements);
      case TEXT_WITH_DATE_AND_DESCRIPTION:
        return this.renderLinksWithDateAndDescription(links, linkElements);
      case TEXT_WITH_METADATA:
        return this.renderLinksWithMetadata(links, linkElements);
      default:
        return this.renderLinkColumns(links);
    }
  }

  render() {
    const useDefault = this.dataLinkedListType === LINK;
    const HeadingTag = this.dataLinkedListHeadingLevel;
    const classMap = {
      'adl-background-color--lightest': useDefault,
      'adl-link-pile__title': useDefault,
      'adl-link-pile__list-heading': !useDefault,
      'adl-h2': this.dataLinkedListHeadingLevel === 'h2',
      'adl-h3': this.dataLinkedListHeadingLevel === 'h3',
      'adl-h4': this.dataLinkedListHeadingLevel === 'h4',
    };

    return (
      <div class="adl-link-pile">
        {this.dataLinkedListTitle && (
          <HeadingTag class={getClassListString(classMap)} innerHTML={this.dataLinkedListTitle} />
        )}
        {this.renderLinkPileContainer()}
        <slot name="data-links"></slot>
      </div>
    );
  }
}
